// ─────────────────────────────────────────────────────────────────────────
//  Login Page Config — Inside D → global sign-in banner management for every dApp.
//
//  dAdmin owns the configuration; each sibling dApp's sign-in screen reads it
//  from GET /api/login-banners/public?app=<key> and paints a rotating panel
//  beside its form. Pick an app at the top, and everything below applies to
//  that app only.
//
//  Per app you can: add banners, edit copy + gradient, enable/disable,
//  drag to reorder, delete, set the rotation interval, and switch the
//  two-step sign-in check on or off.
//
//  Layout is a workbench: an app strip across the top, the banner list and
//  the sign-in settings down the left, and the editor + live panel pinned on
//  the right. One banner is edited at a time, so the panel beside it is
//  always showing the thing being typed into.
//
//  Deliberately NOT the same thing as Inside D → Banner Configuration, which
//  manages image/poll banners on the Inside D portal (dolluzcorp_banners).
//  This page only touches login_banner / login_app_config.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
    FaPlus, FaTrash, FaGripVertical, FaSave, FaUndo, FaUserSlash,
    FaBan, FaCheckCircle, FaTimes, FaLaptop, FaImage, FaUpload,
    FaAlignLeft,
} from "react-icons/fa";
import { dnAlert } from "./utils/dNews_Alert";
import { apiFetch, API_BASE } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./Login_Banner_config.css";

const MIN_ROTATE = 2;
const MAX_ROTATE = 30;
const MIN_TRUST = 1;
const MAX_TRUST = 90;

// A brand-new row starts here. banner_id null marks it as unsaved, which is
// what tells the save handler to POST instead of PUT.
const blankBanner = (app, defaultTo) => ({
    banner_id: null,
    app_key: app,
    tag_label: "NEW",
    headline: "",
    subline: "",
    image_path: null,
    gradient_from: "#0D1B2A",
    gradient_to: defaultTo || "#16334F",
    active: 1,
    _dirty: true,
});

export default function Login_Banner_config({ navSize }) {
    const [apps, setApps] = useState([]);
    const [app, setApp] = useState("");
    const [banners, setBanners] = useState([]);
    const [rotate, setRotate] = useState(3);
    const [savedRotate, setSavedRotate] = useState(3);
    // Two-step sign-in for the selected app.
    const [twoFactor, setTwoFactor] = useState(1);
    const [trustDays, setTrustDays] = useState(14);
    const [savedTwoFactor, setSavedTwoFactor] = useState(1);
    const [savedTrustDays, setSavedTrustDays] = useState(14);
    const [trustedCount, setTrustedCount] = useState(null);
    // Who is currently skipping the code, so one person can be revoked rather
    // than everybody. Loaded only when the panel is opened.
    const [trustOpen, setTrustOpen] = useState(false);
    const [trustRows, setTrustRows] = useState([]);
    const [trustLoading, setTrustLoading] = useState(false);
    const [revoking, setRevoking] = useState(null);
    const [uploading, setUploading] = useState(false);
    // Natural size of the image currently shown, read off the <img> once it
    // loads. Keyed by src so a stale measurement is never shown against a
    // different picture.
    const [imgSize, setImgSize] = useState(null);
    // { gradient_from?: string, gradient_to?: string } - what is currently typed
    // in a hex box, before it is a valid colour.
    const [hexDraft, setHexDraft] = useState({});
    // The three figures under the headline on the sign-in panel. Per app, not
    // per banner - they do not change as the panel rotates.
    const [stats, setStats] = useState([]);
    const [savedStats, setSavedStats] = useState([]);
    // Which kind of banner is being added. Null means the chooser is closed.
    const [addOpen, setAddOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    // Which banner the preview panel is showing. Independent of the row being
    // edited, so you can compare one banner while typing into another.
    const [previewIdx, setPreviewIdx] = useState(0);
    // Active-banner count per app, shown on the app strip. Seeded from /apps
    // and re-patched for the current app after every load.
    const [counts, setCounts] = useState({});

    const appMeta = apps.find((a) => a.key === app);

    // ── Load the app list once, then default to dAdmin ──
    useEffect(() => {
        apiFetch("/api/login-banners/apps")
            .then((r) => r.json())
            .then((rows) => {
                if (!Array.isArray(rows) || rows.length === 0) return;
                setApps(rows);
                setCounts(Object.fromEntries(rows.map((r) => [r.key, r.active_count ?? 0])));
                setApp((cur) => cur || rows.find((a) => a.key === "dAdmin")?.key || rows[0].key);
            })
            .catch(() => dnAlert.error("Could not load the app list"));
    }, []);

    const load = useCallback(async (key) => {
        if (!key) return;
        setLoading(true);
        try {
            const res = await apiFetch(`/api/login-banners?app=${encodeURIComponent(key)}`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Could not load banners");
            setBanners((data.banners || []).map((b) => ({ ...b, _dirty: false })));
            setCounts((c) => ({ ...c, [key]: data.active_count ?? 0 }));
            setRotate(data.rotate_seconds || 3);
            setSavedRotate(data.rotate_seconds || 3);
            const tf = data.two_factor_enabled === undefined ? 1 : Number(data.two_factor_enabled);
            const td = data.trust_days || 14;
            setTwoFactor(tf); setSavedTwoFactor(tf);
            setTrustDays(td); setSavedTrustDays(td);
            const st = Array.isArray(data.panel_stats) ? data.panel_stats : [];
            setStats(st); setSavedStats(st);
            setPreviewIdx(0);

            // How many people would be affected by switching 2FA off or
            // shortening the window - shown next to those controls.
            apiFetch(`/api/login-banners/config/${encodeURIComponent(key)}/trust-count`)
                .then((r) => r.json())
                .then((d) => setTrustedCount(d?.trusted ?? null))
                .catch(() => setTrustedCount(null));
        } catch (e) {
            dnAlert.error("Load failed", e.message);
            setBanners([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(app); }, [app, load]);

    // A draft belongs to the banner it was typed into.
    useEffect(() => { setHexDraft({}); }, [previewIdx]);

    const activeCount = banners.filter((b) => b.active === 1 && b.banner_id).length;
    const dirtyCount = banners.filter((b) => b._dirty).length;

    const patch = (idx, changes) =>
        setBanners((prev) => prev.map((b, i) => (i === idx ? { ...b, ...changes, _dirty: true } : b)));

    // Images are stored the moment they are chosen, because a brand-new banner
    // has no id yet to attach one to. The path is only persisted when the row
    // is saved, so abandoning a draft leaves an unreferenced file - cheap, and
    // far simpler than a two-phase upload.
    const uploadImage = async (idx, file) => {
        if (!file) return;
        setUploading(true);
        try {
            const body = new FormData();
            body.append("image", file);
            const res = await apiFetch("/api/login-banners/upload", { method: "POST", body });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Upload failed");
            patch(idx, { image_path: data.image_path });
            dnAlert.success("Image added", "Save the banner to publish it.");
        } catch (e) {
            dnAlert.error("Could not upload", e.message);
        } finally {
            setUploading(false);
        }
    };

    // Clearing only detaches it here; the file is removed server-side on save,
    // so a discard still gets the picture back.
    const clearImage = (idx) => patch(idx, { image_path: null });

    // Two ways to build a panel, and which one you want is a real decision -
    // so it is asked once, up front, rather than inferred from whichever field
    // happens to get filled in first.
    const addBanner = (method) => {
        setAddOpen(false);
        setBanners((prev) => [...prev, blankBanner(app, appMeta?.defaultTo)]);
        setPreviewIdx(banners.length);
        if (method === "image") {
            dnAlert.success("Draft added", "Upload the image, then save.");
            // The upload input is rendered by the editor that is about to mount.
            setTimeout(() => document.getElementById("lbc-file")?.click(), 60);
        } else {
            dnAlert.success("Draft added", "Give it a headline, then save.");
        }
    };

    // ── Save one row (POST when new, PUT when it already exists) ──
    const saveRow = async (idx) => {
        const b = banners[idx];
        if (!b.headline.trim() && !b.image_path) {
            return dnAlert.warning("Nothing to show", "Add a headline, or upload an image instead.");
        }
        setBusyId(b.banner_id ?? `new-${idx}`);
        try {
            const isNew = !b.banner_id;
            const res = await apiFetch(
                isNew ? "/api/login-banners" : `/api/login-banners/${b.banner_id}`,
                {
                    method: isNew ? "POST" : "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        app_key: app,
                        tag_label: b.tag_label,
                        headline: b.headline,
                        subline: b.subline,
                        image_path: b.image_path || null,
                        gradient_from: b.gradient_from,
                        gradient_to: b.gradient_to,
                        active: b.active,
                    }),
                }
            );
            const data = await res.json();
            if (!res.ok) {
                // The backend returns `details` for field-level problems and a
                // LAST_BANNER error when a deactivate would empty the screen.
                if (Array.isArray(data.details) && data.details.length) {
                    return dnAlert.error(data.message || "Invalid banner", data.details.join(" "));
                }
                throw new Error(data.message || "Save failed");
            }
            dnAlert.success(isNew ? "Banner added" : "Banner saved");
            load(app);
        } catch (e) {
            dnAlert.error("Save failed", e.message);
        } finally {
            setBusyId(null);
        }
    };

    const toggleActive = async (idx) => {
        const b = banners[idx];
        if (b.active === 1 && activeCount <= 1) {
            return dnAlert.error(
                "Could not change status",
                `At least one banner must stay active - this is the last one for ${app}.`
            );
        }
        if (!b.banner_id) return patch(idx, { active: b.active === 1 ? 0 : 1 });
        // Reuse the row save so the last-active guard is enforced server-side.
        patch(idx, { active: b.active === 1 ? 0 : 1 });
        setTimeout(() => saveRowWith(idx, { active: b.active === 1 ? 0 : 1 }), 0);
    };

    // Save with an explicit override, used by the activate/deactivate buttons so
    // they don't depend on state having flushed.
    const saveRowWith = async (idx, override) => {
        const b = { ...banners[idx], ...override };
        setBusyId(b.banner_id);
        try {
            const res = await apiFetch(`/api/login-banners/${b.banner_id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    app_key: app,
                    tag_label: b.tag_label,
                    headline: b.headline,
                    subline: b.subline,
                    image_path: b.image_path || null,
                    gradient_from: b.gradient_from,
                    gradient_to: b.gradient_to,
                    active: b.active,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Save failed");
            dnAlert.success(b.active === 1 ? "Banner activated" : "Banner deactivated");
            load(app);
        } catch (e) {
            dnAlert.error("Could not change status", e.message);
            load(app);
        } finally {
            setBusyId(null);
        }
    };

    const removeRow = async (idx) => {
        const b = banners[idx];
        if (!b.banner_id) {
            return setBanners((prev) => prev.filter((_, i) => i !== idx));
        }
        if (b.active === 1 && activeCount <= 1) {
            return dnAlert.error(
                "Could not delete",
                `At least one banner must stay active - this is the last one for ${app}.`
            );
        }
        const ok = await dnAlert.confirm({
            title: "Delete this banner?",
            message: `"${b.headline}" will stop appearing on the ${app} sign-in screen. This cannot be undone.`,
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        setBusyId(b.banner_id);
        try {
            const res = await apiFetch(`/api/login-banners/${b.banner_id}`, { method: "DELETE" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Delete failed");
            dnAlert.success("Banner deleted");
            load(app);
        } catch (e) {
            dnAlert.error("Delete failed", e.message);
        } finally {
            setBusyId(null);
        }
    };

    // ── Drag to reorder. Unsaved rows can't be reordered server-side, so the
    //    handler saves the order of persisted rows only. ──
    const onDragEnd = async (result) => {
        if (!result.destination || result.destination.index === result.source.index) return;
        const next = [...banners];
        const [moved] = next.splice(result.source.index, 1);
        next.splice(result.destination.index, 0, moved);
        setBanners(next);
        setPreviewIdx(0);

        const order = next.filter((b) => b.banner_id).map((b) => b.banner_id);
        if (order.length === 0) return;
        try {
            const res = await apiFetch(`/api/login-banners/reorder/${encodeURIComponent(app)}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ order }),
            });
            if (!res.ok) throw new Error((await res.json()).message || "Reorder failed");
            dnAlert.success("Order updated");
        } catch (e) {
            dnAlert.error("Could not save the new order", e.message);
            load(app);
        }
    };

    // One endpoint saves the interval and the sign-in settings together, so the
    // two can never end up half-applied.
    const saveConfig = async (patch, successMsg) => {
        try {
            const res = await apiFetch(`/api/login-banners/config/${encodeURIComponent(app)}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rotate_seconds: rotate, ...patch }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Could not save");
            setSavedRotate(data.rotate_seconds);
            setTwoFactor(data.two_factor_enabled);
            setSavedTwoFactor(data.two_factor_enabled);
            setTrustDays(data.trust_days);
            setSavedTrustDays(data.trust_days);
            if (Array.isArray(data.panel_stats)) {
                setStats(data.panel_stats);
                setSavedStats(data.panel_stats);
            }
            dnAlert.success("Saved", successMsg);
        } catch (e) {
            dnAlert.error("Could not save", e.message);
        }
    };

    // A number box you can clear and retype. React leaves the DOM alone when the
    // typed text loosely equals the stored number ("03" == 3) - that is how
    // "030" appeared: clearing stored 0, then typing 30 kept the zero on screen.
    // Held as a number, or "" while the box is empty, and handed back to the
    // input as a string so React compares text with text and redraws cleanly.
    const numChange = (setter) => (e) => {
        const raw = e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
        setter(raw === "" ? "" : Number(raw));
    };
    const numValue = (v) => (v === "" ? "" : String(v));
    const inRange = (v, min, max) => v !== "" && v >= min && v <= max;


    const statsDirty = JSON.stringify(stats) !== JSON.stringify(savedStats);

    const patchStat = (i, key, val) =>
        setStats((prev) => prev.map((r, n) => (n === i ? { ...r, [key]: val } : r)));

    const addStat = () => setStats((prev) => [...prev, { value: "", label: "" }]);
    const removeStat = (i) => setStats((prev) => prev.filter((_, n) => n !== i));

    const saveStats = async () => {
        // A half-filled row would render as a floating number or a stray word,
        // so it is dropped rather than saved.
        const clean = stats
            .map((r) => ({ value: (r.value || "").trim(), label: (r.label || "").trim() }))
            .filter((r) => r.value && r.label);
        await saveConfig(
            { rotate_seconds: savedRotate, panel_stats: clean },
            clean.length
                ? `${app} shows ${clean.length} figure${clean.length === 1 ? "" : "s"} on its sign-in panel.`
                : `${app} shows no figures on its sign-in panel.`
        );
    };

    // One Save for the whole Sign-in settings card. The interval, the two-step
    // switch and the trust window go in a single request, so they can never end
    // up half-applied - which is what this endpoint was built for.
    const settingsDirty =
        rotate !== savedRotate || twoFactor !== savedTwoFactor || trustDays !== savedTrustDays;
    // The days box is locked while two-step is off, so only hold it to its range
    // when it is actually in use - otherwise an empty locked box would block Save
    // with no visible reason.
    const settingsProblem = !inRange(rotate, MIN_ROTATE, MAX_ROTATE)
        ? `Rotation must be ${MIN_ROTATE}-${MAX_ROTATE} seconds`
        : twoFactor === 1 && !inRange(trustDays, MIN_TRUST, MAX_TRUST)
            ? `Days remembered must be ${MIN_TRUST}-${MAX_TRUST}`
            : null;

    const saveSettings = async () => {
        if (settingsProblem) return dnAlert.warning("Check the settings", settingsProblem);

        const turningOff = savedTwoFactor === 1 && twoFactor === 0;
        if (turningOff) {
            const ok = await dnAlert.confirm({
                title: "Turn off two-step verification?",
                message: `Everyone signing in to ${app} will need only their password - no emailed code. The "Remember for N days" control disappears from that sign-in screen.`,
                confirmLabel: "Turn it off",
                danger: true,
            });
            if (!ok) return;
        }

        // Say what actually changed, not everything the card holds.
        const said = [];
        if (rotate !== savedRotate) said.push(`banners rotate every ${rotate}s`);
        if (twoFactor !== savedTwoFactor || trustDays !== savedTrustDays) {
            said.push(twoFactor
                ? `an emailed code is required, remembered for ${trustDays} days`
                : "sign-in is password only");
        }

        await saveConfig(
            {
                rotate_seconds: rotate,
                two_factor_enabled: twoFactor,
                // Locked while two-step is off; keep the saved value if it was left empty.
                trust_days: inRange(trustDays, MIN_TRUST, MAX_TRUST) ? trustDays : savedTrustDays,
            },
            `${app}: ${said.join(", and ")}.`
        );
    };

    // ── Who is currently trusted ─────────────────────────────────────────
    const loadTrusted = useCallback(async (key) => {
        setTrustLoading(true);
        try {
            const res = await apiFetch(`/api/login-banners/config/${encodeURIComponent(key)}/trusted`);
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Could not load");
            setTrustRows(data.rows || []);
            setTrustedCount((data.rows || []).length);
        } catch (e) {
            dnAlert.error("Could not load the list", e.message);
            setTrustRows([]);
        } finally {
            setTrustLoading(false);
        }
    }, []);

    const openTrust = () => {
        setTrustOpen(true);
        loadTrusted(app);
    };

    // Shortening the window does not retroactively shorten trust already
    // granted, so this is what actually makes a change bite. `row` omitted
    // revokes everyone for the app; passing one revokes just that person.
    const revokeTrust = async (row) => {
        const who = row ? (row.name || row.emp_id) : null;
        const ok = await dnAlert.confirm({
            title: row ? `Make ${who} verify again?` : "Make everyone verify again?",
            message: row
                ? `${who} is signed out of ${app} immediately, and asked for an emailed code the next time they sign in.`
                : `Everyone is signed out of ${app} immediately${trustedCount ? ` (${trustedCount} currently skipping the code)` : ""}, and asked for an emailed code the next time they sign in.`,
            confirmLabel: row ? "Revoke" : "Revoke all",
            danger: true,
        });
        if (!ok) return;
        setRevoking(row ? row.emp_id : "__all__");
        try {
            const res = await apiFetch(`/api/login-banners/config/${encodeURIComponent(app)}/revoke-trust`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(row ? { emp_id: row.emp_id } : {}),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Could not revoke");
            dnAlert.success("Revoked", row
                ? `${who} is signed out and will be asked for a code next time.`
                : `${data.revoked} remembered sign-in${data.revoked === 1 ? "" : "s"} cleared, and everyone signed out.`);
            if (row) {
                await loadTrusted(app);
            } else {
                setTrustRows([]);
                setTrustedCount(0);
                setTrustOpen(false);
            }
        } catch (e) {
            dnAlert.error("Could not revoke", e.message);
        } finally {
            setRevoking(null);
        }
    };

    // A date is more useful here as "how much longer", which is the thing an
    // admin is actually deciding about.
    const daysLeft = (iso) => {
        if (!iso) return "-";
        const d = Math.ceil((new Date(iso) - Date.now()) / 86400000);
        return d <= 0 ? "today" : `${d} day${d === 1 ? "" : "s"}`;
    };

    const preview = banners[previewIdx] || banners[0];
    const grad = (b) => `linear-gradient(135deg, ${b.gradient_from} 0%, ${b.gradient_to} 100%)`;
    // Stored as a path so it survives a domain change; resolved against this
    // app's API base for display.
    const imgUrl = (b) => (b?.image_path ? `${API_BASE}${b.image_path}` : null);

    // "1920 × 1080" is only half the answer; the ratio is what tells you
    // whether the panel will crop it. Reduced with gcd, and anything that does
    // not reduce cleanly is shown as a decimal rather than something like
    // 1023:704 that reads as noise.
    // Accepts what people actually paste from a colour picker or a search
    // result: with or without the #, 3-digit shorthand or 6, any case.
    // Returns null for anything that is not yet a colour.
    const normHex = (raw) => {
        const t = String(raw || "").trim().replace(/^#/, "");
        if (/^[0-9a-fA-F]{3}$/.test(t)) {
            return "#" + t.split("").map((c) => c + c).join("").toUpperCase();
        }
        if (/^[0-9a-fA-F]{6}$/.test(t)) return "#" + t.toUpperCase();
        return null;
    };

    const onHexType = (key, raw) => {
        setHexDraft((d) => ({ ...d, [key]: raw }));
        const hex = normHex(raw);
        if (hex) patch(previewIdx, { [key]: hex });
    };

    // Leaving a box that never became a colour puts the real value back, rather
    // than stranding the field on text that was never applied.
    const onHexBlur = (key) => setHexDraft((d) => {
        const { [key]: _drop, ...rest } = d;
        return rest;
    });

    const ratioOf = (w, h) => {
        if (!w || !h) return "";
        const gcd = (a, b) => (b ? gcd(b, a % b) : a);
        const g = gcd(w, h);
        const rw = w / g, rh = h / g;
        return rw <= 40 && rh <= 40 ? `${rw}:${rh}` : `${(w / h).toFixed(2)}:1`;
    };

    return (
        <div className="lbc-container">
            <div className={`lbc-dashboard ${navSize}`}>

                {/* ── Page header ── */}
                <div className="lbc-page-header">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }} />
                        </a>
                        Login Page Config
                    </h2>
                    <p className="lbc-subtitle">
                        The rotating panel on each dApp's sign-in screen. Every banner carries its own
                        gradient, so the panels do not all look alike. Edits are live at the next sign-in.
                    </p>
                </div>

                <div className="lbc-body">

                    {/* ── App strip. Eight apps is few enough to show at once, and
                        the count says which ones still have nothing configured. ── */}
                    <div className="lbc-apps">
                        {apps.map((a) => (
                            <button
                                key={a.key}
                                type="button"
                                className={`lbc-app-tab${a.key === app ? " on" : ""}`}
                                onClick={() => setApp(a.key)}
                                title={`${a.label} - ${counts[a.key] ?? 0} active banner${(counts[a.key] ?? 0) === 1 ? "" : "s"}`}
                            >
                                <span className="lbc-app-dot" style={{ background: a.accent }} />
                                {a.label}
                                <span className="lbc-app-ct">{counts[a.key] ?? 0}</span>
                            </button>
                        ))}
                    </div>

                    <div className="lbc-wb">

                        {/* ══ Left: the list, and the settings that apply to all of them ══ */}
                        <div className="lbc-wb-left">

                            <section className="lbc-card">
                                <header className="lbc-card-h">
                                    <span className="lbc-card-t">Banners</span>
                                    <span className="lbc-card-s">{banners.length} · {activeCount} active</span>
                                    <span className="lbc-spacer" />
                                    <button
                                        type="button"
                                        className="lbc-btn lbc-btn--primary"
                                        onClick={() => setAddOpen(true)}
                                        disabled={!app}
                                    >
                                        <FaPlus /> Add
                                    </button>
                                </header>

                                {loading ? (
                                    <div className="lbc-wb-none">Loading banners…</div>
                                ) : banners.length === 0 ? (
                                    <div className="lbc-wb-none">
                                        No banners for {app} yet. Its sign-in screen uses the built-in fallback.
                                    </div>
                                ) : (
                                    <DragDropContext onDragEnd={onDragEnd}>
                                        <Droppable droppableId="banners">
                                            {(dp) => (
                                                <div className="lbc-wb-list" ref={dp.innerRef} {...dp.droppableProps}>
                                                    {banners.map((b, idx) => {
                                                        const key = b.banner_id ?? `new-${idx}`;
                                                        return (
                                                            <Draggable key={key} draggableId={String(key)} index={idx}>
                                                                {(dr, snap) => (
                                                                    <div
                                                                        ref={dr.innerRef}
                                                                        {...dr.draggableProps}
                                                                        className={`lbc-wb-item${idx === previewIdx ? " on" : ""}${b.active !== 1 ? " dim" : ""}${snap.isDragging ? " drag" : ""}`}
                                                                        onClick={() => setPreviewIdx(idx)}
                                                                    >
                                                                        <span className="lbc-wb-grip" {...dr.dragHandleProps} title="Drag to reorder">
                                                                            <FaGripVertical />
                                                                        </span>
                                                                        <span
                                                                            className={`lbc-wb-chip${b.image_path ? " img" : ""}`}
                                                                            style={b.image_path
                                                                                ? { backgroundImage: `url(${imgUrl(b)})` }
                                                                                : { background: grad(b) }}
                                                                        >
                                                                            {b.active === 1 ? "ON" : "OFF"}
                                                                        </span>
                                                                        <span className="lbc-wb-txt">
                                                                            <span className="lbc-wb-tag">
                                                                                {b.image_path ? "IMAGE" : (b.tag_label || "TAG")}
                                                                            </span>
                                                                            <div className="lbc-wb-head">
                                                                                {b.image_path
                                                                                    ? (b.headline || "Image banner")
                                                                                    : (b.headline || "Untitled banner")}
                                                                            </div>
                                                                        </span>
                                                                        {b._dirty && <span className="lbc-dirty">•</span>}
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {dp.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </section>

                            {/* ── Rotation + two-step, for the whole app ── */}
                            <section className="lbc-card">
                                <header className="lbc-card-h">
                                    <span className="lbc-card-t">Sign-in settings</span>
                                </header>

                                <div className="lbc-wb-set">
                                    <div className="lbc-field">
                                        <label htmlFor="lbc-rot">Rotation interval</label>
                                        <div className="lbc-inline">
                                            <input
                                                id="lbc-rot"
                                                className="lbc-in lbc-in--num"
                                                type="number"
                                                min={MIN_ROTATE}
                                                max={MAX_ROTATE}
                                                value={numValue(rotate)}
                                                onChange={numChange(setRotate)}
                                            />
                                            <span className="lbc-unit">seconds</span>
                                        </div>
                                    </div>

                                    <div className="lbc-field">
                                        <label>Two-step sign-in</label>

                                        <label className="lbc-toggle">
                                            <input
                                                type="checkbox"
                                                checked={twoFactor === 1}
                                                onChange={(e) => setTwoFactor(e.target.checked ? 1 : 0)}
                                            />
                                            <span className="lbc-toggle-track">
                                                <span className="lbc-toggle-knob" />
                                            </span>
                                            <span className="lbc-toggle-txt">
                                                {twoFactor === 1 ? "Required" : "Off"}
                                            </span>
                                        </label>

                                                                                <div className="lbc-inline">
                                            <input
                                                className="lbc-in lbc-in--days"
                                                type="number"
                                                min={MIN_TRUST}
                                                max={MAX_TRUST}
                                                value={numValue(trustDays)}
                                                disabled={twoFactor !== 1}
                                                onChange={numChange(setTrustDays)}
                                            />
                                            <span className="lbc-unit">days remembered</span>
                                        </div>

                                        <div className="lbc-inline">
                                            <button
                                                type="button"
                                                className="lbc-btn lbc-btn--primary"
                                                onClick={saveSettings}
                                                disabled={!settingsDirty || !!settingsProblem}
                                                title={settingsProblem || (settingsDirty ? "Save the sign-in settings" : "No changes to save")}
                                            >
                                                <FaSave /> Save
                                            </button>
                                            <button
                                                type="button"
                                                className="lbc-btn lbc-btn--danger"
                                                onClick={openTrust}
                                                disabled={trustedCount === 0}
                                                title={trustedCount === 0
                                                    ? "Nobody is skipping the code for this app"
                                                    : "See who is skipping the code, and revoke"}
                                            >
                                                <FaUserSlash /> Revoke remembered
                                                {trustedCount ? ` (${trustedCount})` : ""}
                                            </button>
                                        </div>

                                        <div className="lbc-wb-note">
                                            {twoFactor === 1 ? (
                                                <>
                                                    {/* Not every app remembers the same thing: most trust the
                                                        account, dAttendance trusts the browser. Saying the wrong
                                                        one here would misdescribe what the button does. */}
                                                    {appMeta?.trustScope === "device" ? (
                                                        <>Remembering applies to the browser, not the account, so the same
                                                            person is still asked for a code on another machine. </>
                                                    ) : (
                                                        <>Remembering applies to the account, not the browser, so that person
                                                            skips the code from any machine. </>
                                                    )}
                                                    Changing the number only affects sign-ins from now on - use{" "}
                                                    <strong>Revoke remembered</strong> to cut short the ones already granted.
                                                </>
                                            ) : (
                                                <>Two-step is off for {app}: a correct password signs in immediately, and the
                                                    remember-me control is hidden from that sign-in screen.</>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* ── The figures under the headline ── */}
                            <section className="lbc-card">
                                <header className="lbc-card-h">
                                    <span className="lbc-card-t">Panel figures</span>
                                    <span className="lbc-card-s">{stats.length} of 3</span>
                                    <span className="lbc-spacer" />
                                    <button
                                        type="button"
                                        className="lbc-btn lbc-btn--ghost"
                                        onClick={addStat}
                                        disabled={stats.length >= 3}
                                        title={stats.length >= 3 ? "Three is the most the panel fits" : "Add a figure"}
                                    >
                                        <FaPlus /> Add
                                    </button>
                                </header>

                                <div className="lbc-wb-set">
                                    {stats.length === 0 ? (
                                        <div className="lbc-wb-none">
                                            No figures - the sign-in panel shows the rule and nothing under it.
                                        </div>
                                    ) : (
                                        stats.map((r, i) => (
                                            <div className="lbc-stat-row" key={i}>
                                                <input
                                                    className="lbc-in lbc-in--stat"
                                                    value={r.value || ""}
                                                    maxLength={12}
                                                    placeholder="8"
                                                    onChange={(e) => patchStat(i, "value", e.target.value)}
                                                    aria-label={`Figure ${i + 1}`}
                                                />
                                                <input
                                                    className="lbc-in"
                                                    value={r.label || ""}
                                                    maxLength={40}
                                                    placeholder="apps administered"
                                                    onChange={(e) => patchStat(i, "label", e.target.value)}
                                                    aria-label={`Label ${i + 1}`}
                                                />
                                                <button
                                                    type="button"
                                                    className="lbc-btn lbc-btn--danger"
                                                    onClick={() => removeStat(i)}
                                                    title="Remove this figure"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        ))
                                    )}

                                    <div className="lbc-inline">
                                        <button
                                            type="button"
                                            className="lbc-btn lbc-btn--primary"
                                            onClick={saveStats}
                                            disabled={!statsDirty}
                                        >
                                            <FaSave /> Save
                                        </button>
                                        {statsDirty && (
                                            <button
                                                type="button"
                                                className="lbc-btn lbc-btn--ghost"
                                                onClick={() => setStats(savedStats)}
                                            >
                                                <FaUndo /> Discard
                                            </button>
                                        )}
                                    </div>

                                    <div className="lbc-wb-note">
                                        Shown under the headline on every {app} sign-in panel, the same on all
                                        banners. This screen is public, so these must be product facts - never a
                                        live count of employees, payroll or anything else about people.
                                        An image banner shows no figures at all.
                                    </div>
                                </div>
                            </section>
                        </div>

                        {/* ══ Right: what it looks like, and the one banner being edited ══ */}
                        <div className="lbc-wb-right">

                            <div
                                className={`lbc-stage${preview?.image_path ? " lbc-stage--img" : ""}`}
                                style={preview?.image_path
                                    ? { backgroundImage: `url(${imgUrl(preview)})` }
                                    : { background: preview ? grad(preview) : "#0f172a" }}
                            >
                                <span className="lbc-stage-note">{app} sign-in</span>

                                {/* An image banner fills the panel on its own - the
                                    sign-in screens paint no text over it, so neither
                                    does the preview. */}
                                {!preview?.image_path && (
                                    <>
                                        <span className="lbc-stage-tag">
                                            {preview ? (preview.tag_label || "TAG") : "TAG"}
                                        </span>
                                        <h3 className="lbc-stage-h">
                                            {preview ? (preview.headline || "Your headline appears here") : "No banners yet"}
                                        </h3>
                                        <p className="lbc-stage-s">
                                            {preview
                                                ? (preview.subline || "And the supporting line sits underneath it.")
                                                : "Add one to get started."}
                                        </p>
                                    </>
                                )}
                                <div className="lbc-stage-dots">
                                    {banners.map((b, i) => (
                                        <button
                                            key={b.banner_id ?? `new-${i}`}
                                            type="button"
                                            className={`lbc-stage-dot${i === previewIdx ? " on" : ""}${b.active !== 1 ? " off" : ""}`}
                                            onClick={() => setPreviewIdx(i)}
                                            aria-label={`Preview banner ${i + 1}: ${b.headline || "untitled"}`}
                                            title={b.active === 1 ? b.headline : `${b.headline} (inactive - not shown at sign-in)`}
                                        />
                                    ))}
                                </div>
                            </div>

                            {preview && (
                                <section className="lbc-card">
                                    <header className="lbc-card-h">
                                        <span className="lbc-card-t">Editing</span>
                                        <span className="lbc-card-s">{preview.headline || "Untitled banner"}</span>
                                        <span className="lbc-spacer" />
                                        {preview._dirty && <span className="lbc-dirty">Unsaved</span>}
                                    </header>

                                    <div className="lbc-wb-form">

                                        {/* Both ways of filling the panel are shown, in order, each under its
                                            own heading. Only one is ever in use: an image covers the panel, so
                                            the copy above it stops applying - kept, but locked, and labelled as
                                            such rather than silently ignored. */}

                                        <section className={`lbc-mblock${preview.image_path ? " off" : " on"}`}>
                                        <div className="lbc-mhead">
                                            <span className="lbc-mhead-i"><FaAlignLeft /></span>
                                            <span className="lbc-mhead-n">Method 1 · Text and gradient</span>
                                            {!preview.image_path && <span className="lbc-mhead-on">In use</span>}
                                        </div>

                                            {preview.image_path && (
                                                <div className="lbc-wb-off-note">
                                                    <FaImage /> Kept, but not shown while an image is set - remove the
                                                    image below to use these again.
                                                </div>
                                            )}

                                        <div className="lbc-wb-line">
                                            <div className="lbc-field lbc-f-tag">
                                                <label>Tag label</label>
                                                <input
                                                    className="lbc-in"
                                                    disabled={!!preview.image_path}
                                                    value={preview.tag_label || ""}
                                                    maxLength={40}
                                                    placeholder="EVIDENCE"
                                                    onChange={(e) => patch(previewIdx, { tag_label: e.target.value })}
                                                />
                                            </div>
                                            <div className="lbc-field lbc-f-head">
                                                <label>Headline <span className="lbc-req">*</span></label>
                                                <input
                                                    className="lbc-in"
                                                    disabled={!!preview.image_path}
                                                    value={preview.headline || ""}
                                                    maxLength={160}
                                                    placeholder="An assertion is not evidence"
                                                    onChange={(e) => patch(previewIdx, { headline: e.target.value })}
                                                />
                                            </div>
                                            <div className="lbc-field lbc-f-grad">
                                                <label>Gradient</label>
                                                <div className="lbc-grad">
                                                    {[["gradient_from", "Start"], ["gradient_to", "End"]].map(([key, word]) => {
                                                        const typed = hexDraft[key];
                                                        const shown = typed !== undefined
                                                            ? typed
                                                            : (preview[key] || "").toUpperCase();
                                                        // Only flag it once there is something to be wrong about.
                                                        const bad = typed !== undefined && typed !== "" && !normHex(typed);
                                                        return (
                                                            <div className="lbc-grad-row" key={key}>
                                                                <input
                                                                    type="color"
                                                                    disabled={!!preview.image_path}
                                                                    value={preview[key]}
                                                                    onChange={(e) => {
                                                                        onHexBlur(key);
                                                                        patch(previewIdx, { [key]: e.target.value.toUpperCase() });
                                                                    }}
                                                                    title={`${word} ${preview[key]}`}
                                                                    aria-label={`${word} colour picker`}
                                                                />
                                                                <input
                                                                    className={`lbc-in lbc-hex${bad ? " bad" : ""}`}
                                                                    disabled={!!preview.image_path}
                                                                    value={shown}
                                                                    maxLength={7}
                                                                    spellCheck={false}
                                                                    placeholder="#0D1B2A"
                                                                    aria-label={`${word} colour, hex`}
                                                                    title={`${word} - paste a hex like #0D1B2A`}
                                                                    onChange={(e) => onHexType(key, e.target.value)}
                                                                    onBlur={() => onHexBlur(key)}
                                                                    onFocus={(e) => e.target.select()}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="lbc-field" style={{ position: "relative" }}>
                                            <label>Subline</label>
                                            <textarea
                                                className="lbc-in"
                                                disabled={!!preview.image_path}
                                                rows={2}
                                                maxLength={400}
                                                value={preview.subline || ""}
                                                placeholder="A control claimed without proof is recorded as Not Evidenced."
                                                onChange={(e) => patch(previewIdx, { subline: e.target.value })}
                                            />
                                            <span className="lbc-count">{(preview.subline || "").length}/400</span>
                                        </div>
                                        </section>

                                        <section className={`lbc-mblock${preview.image_path ? " on" : ""}`}>
                                        <div className="lbc-mhead">
                                            <span className="lbc-mhead-i"><FaImage /></span>
                                            <span className="lbc-mhead-n">Method 2 · Image</span>
                                            {!!preview.image_path && <span className="lbc-mhead-on">In use</span>}
                                        </div>

                                            {preview.image_path ? (
                                                <div className="lbc-img">
                                                    <img
                                                        src={imgUrl(preview)}
                                                        alt="Banner"
                                                        onLoad={(e) => setImgSize({
                                                            src: e.target.currentSrc || e.target.src,
                                                            w: e.target.naturalWidth,
                                                            h: e.target.naturalHeight,
                                                        })}
                                                    />
                                                    <div className="lbc-img-side">
                                                        {imgSize && imgSize.src === imgUrl(preview) && (
                                                            <div className="lbc-img-meta">
                                                                <span className="lbc-img-dim">
                                                                    {imgSize.w} × {imgSize.h}
                                                                </span>
                                                                <span className="lbc-img-ratio">
                                                                    {ratioOf(imgSize.w, imgSize.h)}
                                                                </span>
                                                                {/* The panel is a tall-ish landscape box and crops to
                                                                    fill, so a portrait image loses its sides. Worth
                                                                    saying before it is published, not after. */}
                                                                {imgSize.h > imgSize.w && (
                                                                    <span className="lbc-img-warn">
                                                                        Portrait - the panel will crop the top and bottom
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="lbc-img-note">
                                                            This image fills the sign-in panel on its own. No tag,
                                                            headline, subline or gradient is shown while it is set.
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="lbc-btn lbc-btn--danger"
                                                            onClick={() => clearImage(previewIdx)}
                                                        >
                                                            <FaTimes /> Remove image
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <label className="lbc-drop">
                                                    <input
                                                        id="lbc-file"
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                                                        disabled={uploading}
                                                        onChange={(e) => {
                                                            uploadImage(previewIdx, e.target.files?.[0]);
                                                            e.target.value = "";
                                                        }}
                                                    />
                                                    <FaUpload />
                                                    <span className="lbc-drop-t">
                                                        {uploading ? "Uploading…" : "Upload an image instead of text"}
                                                    </span>
                                                    <span className="lbc-drop-s">PNG, JPG, WEBP, GIF or AVIF · up to 5MB</span>
                                                    <span className="lbc-drop-s">
                                                        Landscape, around 3:2 - the panel crops to fill, so keep
                                                        anything important near the centre
                                                    </span>
                                                </label>
                                            )}
                                        </section>
                                    </div>

                                    <div className="lbc-wb-acts">
                                        {/* The button says what it will do, not what
                                            the banner currently is. */}
                                        <button
                                            type="button"
                                            className={`lbc-btn ${preview.active === 1 ? "lbc-btn--warn" : "lbc-btn--ok"}`}
                                            onClick={() => toggleActive(previewIdx)}
                                            disabled={busyId !== null || !preview.banner_id}
                                            title={preview.active === 1
                                                ? (activeCount <= 1 ? "This is the last active banner - it cannot be switched off" : "Hide from the sign-in screen")
                                                : "Show on the sign-in screen"}
                                        >
                                            {preview.active === 1
                                                ? <><FaBan /> Disable</>
                                                : <><FaCheckCircle /> Enable</>}
                                        </button>

                                        <button
                                            type="button"
                                            className="lbc-btn lbc-btn--primary"
                                            onClick={() => saveRow(previewIdx)}
                                            disabled={busyId !== null || !preview._dirty}
                                            title={preview._dirty ? "Save this banner" : "No unsaved changes"}
                                        >
                                            <FaSave /> {preview.banner_id ? "Save" : "Add"}
                                        </button>

                                        {preview.banner_id && (
                                            <button
                                                type="button"
                                                className="lbc-btn lbc-btn--ghost"
                                                onClick={() => load(app)}
                                                disabled={busyId !== null || !preview._dirty}
                                                title="Discard unsaved edits"
                                            >
                                                <FaUndo /> Discard
                                            </button>
                                        )}

                                        <button
                                            type="button"
                                            className="lbc-btn lbc-btn--danger"
                                            onClick={() => removeRow(previewIdx)}
                                            disabled={busyId !== null}
                                            title={preview.banner_id && preview.active === 1 && activeCount <= 1
                                                ? "This is the last active banner - it cannot be deleted"
                                                : "Delete this banner"}
                                        >
                                            <FaTrash /> Delete
                                        </button>
                                    </div>
                                </section>
                            )}

                            {/* The rule the backend enforces, stated where it matters. */}
                            {banners.length > 0 && (
                                <div className="lbc-guard">
                                    At least one banner must stay active for <strong>{app}</strong>. The last active one
                                    cannot be deactivated or deleted, because its sign-in screen would have nothing to show.
                                    {dirtyCount > 0 && <> · <strong>{dirtyCount}</strong> row{dirtyCount !== 1 ? "s have" : " has"} unsaved changes.</>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── How should this banner be built? ── */}
            {addOpen && (
                <div className="lbc-modal-back" onClick={() => setAddOpen(false)}>
                    <div className="lbc-modal lbc-modal--sm" onClick={(e) => e.stopPropagation()}>
                        <header className="lbc-modal-h">
                            <div>
                                <div className="lbc-modal-t">Add a banner to {app}</div>
                                <div className="lbc-modal-s">Two ways to fill the panel. You can change your mind later.</div>
                            </div>
                            <button type="button" className="lbc-modal-x" onClick={() => setAddOpen(false)} title="Close">
                                <FaTimes />
                            </button>
                        </header>

                        <div className="lbc-methods">
                            <button type="button" className="lbc-method" onClick={() => addBanner("text")}>
                                <span className="lbc-method-i"><FaAlignLeft /></span>
                                <span className="lbc-method-n">Method 1 · Text and gradient</span>
                                <span className="lbc-method-d">
                                    A tag, a headline and a subline over a two-colour gradient you choose.
                                    The figures below the headline come from Panel figures.
                                </span>
                            </button>

                            <button type="button" className="lbc-method" onClick={() => addBanner("image")}>
                                <span className="lbc-method-i"><FaImage /></span>
                                <span className="lbc-method-n">Method 2 · Image</span>
                                <span className="lbc-method-d">
                                    One image fills the whole panel. No tag, headline, subline, figures or
                                    gradient are shown over it. Landscape, around 3:2, up to 5MB.
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Who is currently skipping the code ──────────────────────────
                Deliberately a list, not just a count: revoking everybody to
                clear one person is the kind of blunt instrument that stops
                admins using the control at all. */}
            {trustOpen && (
                <div className="lbc-modal-back" onClick={() => setTrustOpen(false)}>
                    <div className="lbc-modal" onClick={(e) => e.stopPropagation()}>
                        <header className="lbc-modal-h">
                            <div>
                                <div className="lbc-modal-t">Remembered sign-ins · {app}</div>
                                <div className="lbc-modal-s">
                                    {appMeta?.trustScope === "device"
                                        ? "Each row is one person; the count is how many browsers they have trusted."
                                        : "Each person here skips the emailed code from any machine until it expires."}
                                </div>
                            </div>
                            <button type="button" className="lbc-modal-x" onClick={() => setTrustOpen(false)} title="Close">
                                <FaTimes />
                            </button>
                        </header>

                        <div className="lbc-modal-body">
                            {trustLoading ? (
                                <div className="lbc-wb-none">Loading…</div>
                            ) : trustRows.length === 0 ? (
                                <div className="lbc-wb-none">Nobody is currently skipping the code for {app}.</div>
                            ) : (
                                <table className="lbc-trust">
                                    <thead>
                                        <tr>
                                            <th>Employee</th>
                                            <th>Department</th>
                                            {appMeta?.trustScope === "device" && <th>Browsers</th>}
                                            <th>Expires in</th>
                                            <th>Last used</th>
                                            <th aria-label="Revoke" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trustRows.map((r) => (
                                            <tr key={r.emp_id}>
                                                <td>
                                                    <div className="lbc-trust-name">{r.name || r.emp_id}</div>
                                                    <div className="lbc-trust-sub">{r.emp_id}{r.email ? ` · ${r.email}` : ""}</div>
                                                </td>
                                                <td>{r.department || "-"}</td>
                                                {appMeta?.trustScope === "device" && (
                                                    <td><span className="lbc-trust-dev"><FaLaptop /> {r.devices}</span></td>
                                                )}
                                                <td>{daysLeft(r.expires_at)}</td>
                                                <td>{r.last_used ? new Date(r.last_used).toLocaleDateString() : "-"}</td>
                                                <td className="lbc-trust-act">
                                                    <button
                                                        type="button"
                                                        className="lbc-btn lbc-btn--danger"
                                                        onClick={() => revokeTrust(r)}
                                                        disabled={revoking !== null}
                                                    >
                                                        {revoking === r.emp_id ? "Revoking…" : "Revoke"}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <footer className="lbc-modal-f">
                            <span className="lbc-modal-count">
                                {trustRows.length} {trustRows.length === 1 ? "person" : "people"}
                            </span>
                            <button type="button" className="lbc-btn lbc-btn--ghost" onClick={() => setTrustOpen(false)}>
                                Close
                            </button>
                            <button
                                type="button"
                                className="lbc-btn lbc-btn--danger"
                                onClick={() => revokeTrust()}
                                disabled={revoking !== null || trustRows.length === 0}
                            >
                                {revoking === "__all__" ? "Revoking…" : <><FaUserSlash /> Revoke all</>}
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
}
